import React, { useState, useContext, useEffect, useCallback, useRef, useMemo } from "react";
import { useTranslation } from 'react-i18next';

import './style.css';
import '../../styles/page-titles.css';
import { SidebarContext } from "../../AppRoutes";
import PermissionGate from "../../services/PermissionGate";

import { Modal } from "../../components/Modal";
import ModalAddCustomResponse from "../../components/ModalAddCustomResponse";
import ModalEditCustomResponse from "../../components/ModalEditCustomResponse";
import styled from 'styled-components';
import { FaEdit, FaTrash, FaPlus, FaSearch, FaToggleOn, FaToggleOff, FaRobot, FaWhatsapp, FaComments, FaCheck, FaTimes, FaInfoCircle, FaSave, FaUndo } from "react-icons/fa";
import { MdQuestionAnswer } from "react-icons/md";
import { BiMessageDetail } from "react-icons/bi";
import { BsToggleOn, BsToggleOff } from "react-icons/bs";

import {
    getCustomResponses,
    addCustomResponse,
    updateCustomResponse,
    deleteCustomResponse,
    getOrderbotConfig,
    updateOrderbotConfig,
    getBotContext,
    updateBotContext,
    getCustomOrderMessages,
    updateCustomOrderMessages
} from "../../services/api";
import { toast } from "react-toastify";

const PageContainer = styled.div`
    display: flex;
    margin-left: ${({ $sidebar }) => ($sidebar ? '250px' : '100px')};
    min-height: 100vh;
    transition: margin-left 200ms ease;
    background: #ffffff;

    @media (max-width: 880px) {
        margin-left: 0;
    }
`;

const ContentWrapper = styled.div`
    width: 100%;
    padding: 24px;

    @media (max-width: 768px) {
        padding: 16px;
    }
`;

// Chaves e emojis das mensagens automáticas. Os TEXTOS (labels, "quando", padrões, exemplos)
// vêm do i18n: messages.fields.* / messages.vars.* / messages.defaults.* / messages.example.*
const CUSTOM_MESSAGE_KEYS = ['finalizado', 'em_producao', 'pronto_retirada', 'saiu_entrega', 'novo_pedido_saudacao', 'novo_pedido_agradecimento'];
const CUSTOM_MESSAGE_EMOJIS = {
    finalizado: '✅', em_producao: '👨‍🍳', pronto_retirada: '🏪',
    saiu_entrega: '🛵', novo_pedido_saudacao: '🤩', novo_pedido_agradecimento: '😉',
};
const CUSTOM_VAR_KEYS = ['numeroPedido', 'cliente', 'loja', 'total'];
const CUSTOM_MSG_MAX = 500;

// Substitui as variáveis pelos valores de exemplo (vindos do i18n) na prévia.
function fillPreviewVars(template, lojaName, example) {
    return (template || '')
        .replace(/\{numeroPedido\}/g, example.numeroPedido)
        .replace(/\{numero\}/g, example.numeroPedido)
        .replace(/\{cliente\}/g, example.cliente)
        .replace(/\{loja\}/g, lojaName || example.loja)
        .replace(/\{total\}/g, example.total);
}
// Converte *texto* em negrito (como o WhatsApp faz) para a prévia.
function renderWhatsappBold(line, keyPrefix) {
    return line.split(/(\*[^*]+\*)/g).map((part, idx) => {
        if (part.length > 1 && part.startsWith('*') && part.endsWith('*')) {
            return <strong key={`${keyPrefix}-${idx}`}>{part.slice(1, -1)}</strong>;
        }
        return <React.Fragment key={`${keyPrefix}-${idx}`}>{part}</React.Fragment>;
    });
}

const RoboCfg = () => {
    const { t } = useTranslation('botConfig');

    // Mensagens automáticas: textos e padrões montados a partir do i18n.
    const defaultTemplates = useMemo(() => {
        const obj = {};
        CUSTOM_MESSAGE_KEYS.forEach((k) => { obj[k] = t(`messages.defaults.${k}`); });
        return obj;
    }, [t]);
    const messageFields = useMemo(() => CUSTOM_MESSAGE_KEYS.map((k) => ({
        key: k,
        emoji: CUSTOM_MESSAGE_EMOJIS[k],
        label: t(`messages.fields.${k}.label`),
        when: t(`messages.fields.${k}.when`),
        defaultTemplate: t(`messages.defaults.${k}`),
    })), [t]);
    const messageVars = useMemo(() => CUSTOM_VAR_KEYS.map((k) => ({
        tag: `{${k}}`,
        label: t(`messages.vars.${k}`),
        example: t(`messages.example.${k}`),
    })), [t]);
    const previewExample = useMemo(() => ({
        numeroPedido: t('messages.example.numeroPedido'),
        cliente: t('messages.example.cliente'),
        loja: t('messages.example.loja'),
        total: t('messages.example.total'),
    }), [t]);

    const empresa = localStorage.getItem('empresa');
    const empresaParse = JSON.parse(empresa);
    const empresaObjId = empresaParse._id; // eslint-disable-line no-unused-vars

    const { sidebar } = useContext(SidebarContext);

    // State para controle de abas
    const [activeTab, setActiveTab] = useState('orderbot'); // 'orderbot' | 'context' | 'responses'

    // State para OrderBot
    const [orderbotEnabled, setOrderbotEnabled] = useState(false);
    const [orderbotLoading, setOrderbotLoading] = useState(false);
    const [orderbotSaving, setOrderbotSaving] = useState(false);

    // State para Bot Context (descricao livre do restaurante)
    const BOT_CONTEXT_MAX = 600;
    const [botContextDescription, setBotContextDescription] = useState('');
    const [botContextOriginal, setBotContextOriginal] = useState('');
    const [botContextUpdatedAt, setBotContextUpdatedAt] = useState(null);
    const [botContextLoading, setBotContextLoading] = useState(false);
    const [botContextSaving, setBotContextSaving] = useState(false);

    // State para Mensagens Automáticas personalizadas
    const [customMessages, setCustomMessages] = useState(defaultTemplates);
    const [customMessagesOriginal, setCustomMessagesOriginal] = useState(defaultTemplates);
    const [customMessagesLoading, setCustomMessagesLoading] = useState(false);
    const [customMessagesSaving, setCustomMessagesSaving] = useState(false);
    const [selectedMessageType, setSelectedMessageType] = useState('finalizado');
    const messageRefs = useRef({});

    // State para Respostas Personalizadas
    const [customResponses, setCustomResponses] = useState([]);
    const [filteredResponses, setFilteredResponses] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingData, setEditingData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(5);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteIndex, setDeleteIndex] = useState(null);

    // ============ ORDERBOT CONFIG ============
    const fetchOrderbotConfig = useCallback(async () => {
        try {
            setOrderbotLoading(true);
            const response = await getOrderbotConfig();
            process.env.REACT_APP_ISDEVELOP === 'true' && console.log("[ROBO-CONFIG] OrderBot config:", response.data);
            setOrderbotEnabled(response.data.orderbot_enabled === true);
        } catch (error) {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.error("Erro ao buscar config do OrderBot:", error);
            toast.error(t('toasts.orderbotLoadError'));
        } finally {
            setOrderbotLoading(false);
        }
    }, [t]);

    const handleToggleOrderbot = async () => {
        try {
            setOrderbotSaving(true);
            const newValue = !orderbotEnabled;
            await updateOrderbotConfig(newValue);
            setOrderbotEnabled(newValue);
            toast.success(
                newValue
                    ? t('toasts.orderbotEnabled')
                    : t('toasts.orderbotDisabled')
            );
        } catch (error) {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.error("Erro ao atualizar OrderBot:", error);
            toast.error(t('toasts.orderbotUpdateError'));
        } finally {
            setOrderbotSaving(false);
        }
    };

    // ============ BOT CONTEXT ============
    const fetchBotContext = useCallback(async () => {
        try {
            setBotContextLoading(true);
            const response = await getBotContext();
            const desc = response.data?.bot_context?.description || '';
            setBotContextDescription(desc);
            setBotContextOriginal(desc);
            setBotContextUpdatedAt(response.data?.bot_context?.updatedAt || null);
        } catch (error) {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.error("Erro ao buscar bot context:", error);
            toast.error(t('toasts.contextLoadError'));
        } finally {
            setBotContextLoading(false);
        }
    }, [t]);

    const handleSaveBotContext = async () => {
        if (botContextDescription.length > BOT_CONTEXT_MAX) {
            toast.warn(t('toasts.contextTooLong'));
            return;
        }
        try {
            setBotContextSaving(true);
            const response = await updateBotContext(botContextDescription);
            const saved = response.data?.bot_context?.description ?? botContextDescription;
            setBotContextDescription(saved);
            setBotContextOriginal(saved);
            setBotContextUpdatedAt(response.data?.bot_context?.updatedAt || new Date().toISOString());
            toast.success(t('toasts.contextSaved'));
        } catch (error) {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.error("Erro ao salvar bot context:", error);
            toast.error(t('toasts.contextSaveError'));
        } finally {
            setBotContextSaving(false);
        }
    };

    const isBotContextDirty = botContextDescription !== botContextOriginal;

    // ============ MENSAGENS AUTOMÁTICAS ============
    const fetchCustomMessages = useCallback(async () => {
        try {
            setCustomMessagesLoading(true);
            const response = await getCustomOrderMessages();
            const m = response.data?.custom_order_messages || {};
            // Pré-preenche com o texto padrão quando a loja ainda não personalizou.
            const loaded = { ...defaultTemplates };
            Object.keys(defaultTemplates).forEach((k) => {
                loaded[k] = (m[k] && m[k].trim()) ? m[k] : defaultTemplates[k];
            });
            setCustomMessages(loaded);
            setCustomMessagesOriginal(loaded);
        } catch (error) {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.error("Erro ao buscar mensagens automáticas:", error);
            toast.error(t('messages.toastLoadError'));
        } finally {
            setCustomMessagesLoading(false);
        }
    }, [defaultTemplates, t]);

    const handleSaveCustomMessages = async () => {
        try {
            setCustomMessagesSaving(true);
            const response = await updateCustomOrderMessages(customMessages);
            const m = response.data?.custom_order_messages || customMessages;
            const saved = { ...defaultTemplates };
            Object.keys(defaultTemplates).forEach((k) => {
                saved[k] = (m[k] && m[k].trim()) ? m[k] : defaultTemplates[k];
            });
            setCustomMessages(saved);
            setCustomMessagesOriginal(saved);
            toast.success(t('messages.toastSaveSuccess'));
        } catch (error) {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.error("Erro ao salvar mensagens automáticas:", error);
            toast.error(t('messages.toastSaveError'));
        } finally {
            setCustomMessagesSaving(false);
        }
    };

    // Insere a variável (ex.: {numeroPedido}) na posição do cursor do textarea correspondente
    const insertVariable = (key, variable) => {
        const el = messageRefs.current[key];
        const hasSel = el && typeof el.selectionStart === 'number';
        const start = hasSel ? el.selectionStart : null;
        const end = hasSel ? el.selectionEnd : null;
        setCustomMessages((prev) => {
            const value = prev[key] || '';
            const next = hasSel
                ? value.slice(0, start) + variable + value.slice(end)
                : value + variable;
            return { ...prev, [key]: next.slice(0, CUSTOM_MSG_MAX) };
        });
        if (hasSel) {
            requestAnimationFrame(() => {
                try { el.focus(); const pos = start + variable.length; el.setSelectionRange(pos, pos); } catch (e) { /* noop */ }
            });
        }
    };

    // Restaura uma mensagem específica para o texto padrão do sistema
    const handleRestoreDefault = (key) => {
        setCustomMessages((prev) => ({ ...prev, [key]: defaultTemplates[key] }));
    };

    const isCustomMessagesDirty = JSON.stringify(customMessages) !== JSON.stringify(customMessagesOriginal);

    // ============ CUSTOM RESPONSES ============
    const fetchCustomResponses = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getCustomResponses();
            process.env.REACT_APP_ISDEVELOP === 'true' && console.log("[ROBO-CONFIG] Dados recebidos do backend:", response.data);
            setCustomResponses(response.data.customResponses || []);
        } catch (error) {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.error("Erro ao buscar respostas personalizadas:", error);
            toast.error(t('toasts.responsesLoadError'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    // Carregar dados iniciais
    useEffect(() => {
        fetchOrderbotConfig();
        fetchBotContext();
        fetchCustomResponses();
        fetchCustomMessages();
    }, [fetchOrderbotConfig, fetchBotContext, fetchCustomResponses, fetchCustomMessages]);

    // Filtrar respostas baseado na busca
    useEffect(() => {
        if (searchTerm) {
            const filtered = customResponses.filter(response =>
                response.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                response.response.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredResponses(filtered);
        } else {
            setFilteredResponses(customResponses);
        }
    }, [searchTerm, customResponses]);

    const parseCustomResponse = (responseObj) => {
        if (typeof responseObj === 'object' && responseObj.question && responseObj.response) {
            return {
                question: responseObj.question,
                response: responseObj.response
            };
        }
        return {
            question: t('responses.card.questionNotFound'),
            response: t('responses.card.responseNotFound')
        };
    };

    const handleAddResponse = async (values, { setSubmitting, resetForm }) => {
        try {
            await addCustomResponse(values.question, values.response);
            toast.success(t('toasts.responseAdded'));
            setIsAddModalOpen(false);
            resetForm();
            fetchCustomResponses();
        } catch (error) {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.error("Erro ao adicionar resposta:", error);
            toast.error(t('toasts.responseAddError'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditResponse = async (values, { setSubmitting }) => {
        try {
            await updateCustomResponse(editingData._id, values.question, values.response);
            toast.success(t('toasts.responseUpdated'));
            setIsEditModalOpen(false);
            setEditingData(null);
            fetchCustomResponses();
        } catch (error) {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.error("Erro ao atualizar resposta:", error);
            toast.error(t('toasts.responseUpdateError'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleActive = async (responseObj) => {
        try {
            await updateCustomResponse(responseObj._id, responseObj.question, responseObj.response, !responseObj.active);
            toast.success(!responseObj.active ? t('toasts.responseActivated') : t('toasts.responseDeactivated'));
            fetchCustomResponses();
        } catch (error) {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.error("Erro ao alterar status da resposta:", error);
            toast.error(t('toasts.responseToggleError'));
        }
    };

    const handleDeleteResponse = async () => {
        if (deleteIndex !== null) {
            try {
                await deleteCustomResponse(deleteIndex);
                toast.success(t('toasts.responseDeleted'));
                setShowDeleteConfirm(false);
                setDeleteIndex(null);
                fetchCustomResponses();
                const totalPages = Math.ceil((customResponses.length - 1) / itemsPerPage);
                if (currentPage > totalPages && totalPages > 0) {
                    setCurrentPage(totalPages);
                }
            } catch (error) {
                process.env.REACT_APP_ISDEVELOP === 'true' && console.error("Erro ao remover resposta:", error);
                toast.error(t('toasts.responseDeleteError'));
            }
        }
    };

    const confirmDelete = (responseObj) => {
        setDeleteIndex(responseObj._id);
        setShowDeleteConfirm(true);
    };

    const openEditModal = (responseObj) => {
        const parsed = parseCustomResponse(responseObj);
        process.env.REACT_APP_ISDEVELOP === 'true' && console.log("[ROBO-CONFIG] === DEBUG OPEN EDIT MODAL ===");
        process.env.REACT_APP_ISDEVELOP === 'true' && console.log("[ROBO-CONFIG] ResponseObj completo:", responseObj);

        setEditingData({
            ...parsed,
            _id: responseObj._id,
            id: responseObj.id
        });
        setIsEditModalOpen(true);
    };

    // Paginacao
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredResponses.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredResponses.length / itemsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    return (
        <>
            <PermissionGate permissions={['default']}>

                <PageContainer $sidebar={sidebar}>
                    <ContentWrapper>
                        {/* Header da pagina */}
                        <div className="page-title-with-subtitle">
                            <div className="page-title-with-icon">
                                <div className="page-icon"><FaRobot /></div>
                                <h1 className="page-title">{t('pageTitle')}</h1>
                            </div>
                            <p className="page-subtitle">{t('pageSubtitle')}</p>
                        </div>

                        {/* Sistema de Abas */}
                        <div className="robo-tabs-container">
                            <button
                                className={`robo-tab-button ${activeTab === 'orderbot' ? 'active' : ''}`}
                                onClick={() => setActiveTab('orderbot')}
                            >
                                <span className="robo-tab-number">1</span>
                                <FaWhatsapp className="robo-tab-icon" />
                                {t('tabs.orderbot')}
                            </button>
                            <button
                                className={`robo-tab-button ${activeTab === 'context' ? 'active' : ''}`}
                                onClick={() => setActiveTab('context')}
                            >
                                <span className="robo-tab-number">2</span>
                                <FaInfoCircle className="robo-tab-icon" />
                                {t('tabs.context')}
                            </button>
                            <button
                                className={`robo-tab-button ${activeTab === 'responses' ? 'active' : ''}`}
                                onClick={() => setActiveTab('responses')}
                            >
                                <span className="robo-tab-number">3</span>
                                <FaComments className="robo-tab-icon" />
                                {t('tabs.responses')}
                            </button>
                            <button
                                className={`robo-tab-button ${activeTab === 'messages' ? 'active' : ''}`}
                                onClick={() => setActiveTab('messages')}
                            >
                                <span className="robo-tab-number">4</span>
                                <BiMessageDetail className="robo-tab-icon" />
                                {t('tabs.messages')}
                            </button>
                        </div>

                        {/* Conteudo das Abas */}
                        <div className="robo-tab-content">

                            {/* ============ ABA ORDERBOT ============ */}
                            {activeTab === 'orderbot' && (
                                <div className="robo-orderbot-section">
                                    <div className="robo-orderbot-card">
                                        <div className="robo-orderbot-header">
                                            <div className="robo-orderbot-icon-wrapper">
                                                <FaWhatsapp className="robo-orderbot-main-icon" />
                                            </div>
                                            <div className="robo-orderbot-title">
                                                <h2>{t('orderbot.title')}</h2>
                                                <p>{t('orderbot.description')}</p>
                                            </div>
                                        </div>

                                        <div className="robo-orderbot-content">
                                            <div className="robo-orderbot-info">
                                                <div className="robo-orderbot-feature">
                                                    <FaCheck className="robo-feature-icon success" />
                                                    <span>{t('orderbot.features.automated')}</span>
                                                </div>
                                                <div className="robo-orderbot-feature">
                                                    <FaCheck className="robo-feature-icon success" />
                                                    <span>{t('orderbot.features.naturalLanguage')}</span>
                                                </div>
                                                <div className="robo-orderbot-feature">
                                                    <FaCheck className="robo-feature-icon success" />
                                                    <span>{t('orderbot.features.integrated')}</span>
                                                </div>
                                                <div className="robo-orderbot-feature">
                                                    <FaCheck className="robo-feature-icon success" />
                                                    <span>{t('orderbot.features.automatic')}</span>
                                                </div>
                                            </div>

                                            <div className="robo-orderbot-toggle-section">
                                                <div className="robo-orderbot-status">
                                                    <span className="robo-status-label">{t('orderbot.status.label')}</span>
                                                    <span className={`robo-status-badge ${orderbotEnabled ? 'enabled' : 'disabled'}`}>
                                                        {orderbotEnabled ? (
                                                            <>
                                                                <FaCheck /> {t('orderbot.status.enabled')}
                                                            </>
                                                        ) : (
                                                            <>
                                                                <FaTimes /> {t('orderbot.status.disabled')}
                                                            </>
                                                        )}
                                                    </span>
                                                </div>

                                                <button
                                                    className={`robo-orderbot-toggle-btn ${orderbotEnabled ? 'enabled' : 'disabled'}`}
                                                    onClick={handleToggleOrderbot}
                                                    disabled={orderbotLoading || orderbotSaving}
                                                >
                                                    {orderbotLoading || orderbotSaving ? (
                                                        <span className="robo-toggle-loading">
                                                            <div className="robo-mini-spinner"></div>
                                                            {orderbotSaving ? t('orderbot.buttons.saving') : t('orderbot.buttons.loading')}
                                                        </span>
                                                    ) : (
                                                        <>
                                                            {orderbotEnabled ? (
                                                                <>
                                                                    <BsToggleOn className="robo-toggle-icon" />
                                                                    {t('orderbot.buttons.disable')}
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <BsToggleOff className="robo-toggle-icon" />
                                                                    {t('orderbot.buttons.enable')}
                                                                </>
                                                            )}
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>

                                        <div className="robo-orderbot-footer">
                                            <p>
                                                <strong>{t('orderbot.warning').split(':')[0]}:</strong> {t('orderbot.warning').split(':')[1]}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ============ ABA SOBRE O RESTAURANTE (BOT CONTEXT) ============ */}
                            {activeTab === 'context' && (
                                <div className="robo-context-section">
                                    <div className="robo-context-card">
                                        <div className="robo-context-header">
                                            <div className="robo-context-icon-wrapper">
                                                <FaInfoCircle className="robo-context-main-icon" />
                                            </div>
                                            <div className="robo-context-title">
                                                <h2>{t('context.title')}</h2>
                                                <p>{t('context.description')}</p>
                                            </div>
                                        </div>

                                        <div className="robo-context-content">
                                            {botContextLoading ? (
                                                <div className="robo-loading-container">
                                                    <div className="robo-loading-spinner"></div>
                                                    <p>{t('responses.loading')}</p>
                                                </div>
                                            ) : (
                                                <>
                                                    <label className="robo-context-label" htmlFor="bot-context-textarea">
                                                        {t('context.fieldLabel')}
                                                    </label>
                                                    <textarea
                                                        id="bot-context-textarea"
                                                        className="robo-context-textarea"
                                                        value={botContextDescription}
                                                        onChange={(e) => setBotContextDescription(e.target.value.slice(0, BOT_CONTEXT_MAX))}
                                                        placeholder={t('context.placeholder')}
                                                        rows={10}
                                                        maxLength={BOT_CONTEXT_MAX}
                                                        disabled={botContextSaving}
                                                    />
                                                    <div className="robo-context-meta">
                                                        <span className={`robo-context-charcount ${botContextDescription.length >= BOT_CONTEXT_MAX ? 'limit' : ''}`}>
                                                            {t('context.charCount', { current: botContextDescription.length, max: BOT_CONTEXT_MAX })}
                                                        </span>
                                                        <span className="robo-context-updated">
                                                            {botContextUpdatedAt
                                                                ? t('context.lastUpdated', { date: new Date(botContextUpdatedAt).toLocaleString() })
                                                                : t('context.neverUpdated')}
                                                        </span>
                                                    </div>

                                                    <div className="robo-context-hint">
                                                        <FaInfoCircle /> <span>{t('context.hint')}</span>
                                                    </div>

                                                    <div className="robo-context-actions">
                                                        <button
                                                            className="robo-context-save-btn"
                                                            onClick={handleSaveBotContext}
                                                            disabled={botContextSaving || !isBotContextDirty}
                                                        >
                                                            {botContextSaving ? (
                                                                <span className="robo-toggle-loading">
                                                                    <div className="robo-mini-spinner"></div>
                                                                    {t('context.saving')}
                                                                </span>
                                                            ) : (
                                                                <>
                                                                    <FaSave /> {t('context.save')}
                                                                </>
                                                            )}
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ============ ABA MENSAGENS AUTOMÁTICAS ============ */}
                            {activeTab === 'messages' && (
                                <div className="robo-context-section">
                                    <div className="robo-context-card">
                                        <div className="robo-context-header">
                                            <div className="robo-context-icon-wrapper">
                                                <BiMessageDetail className="robo-context-main-icon" />
                                            </div>
                                            <div className="robo-context-title">
                                                <h2>{t('messages.title')}</h2>
                                                <p>{t('messages.subtitle')}</p>
                                            </div>
                                        </div>

                                        <div className="robo-context-content">
                                            {customMessagesLoading ? (
                                                <div className="robo-loading-container">
                                                    <div className="robo-loading-spinner"></div>
                                                    <p>{t('messages.loading')}</p>
                                                </div>
                                            ) : (
                                                <>
                                                    {/* Bloco de ajuda amigável */}
                                                    <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: 16, marginBottom: 20 }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, color: '#166534', marginBottom: 8, fontSize: 14 }}>
                                                            <FaWhatsapp /> {t('messages.help.title')}
                                                        </div>
                                                        <ul style={{ margin: 0, paddingLeft: 18, color: '#15803d', fontSize: 13, lineHeight: 1.8 }}>
                                                            <li>{t('messages.help.item1')}</li>
                                                            <li>{t('messages.help.item2')}</li>
                                                            <li>{t('messages.help.item3')}</li>
                                                            <li>{t('messages.help.item4')}</li>
                                                        </ul>
                                                    </div>

                                                    {/* Menu: seleção do tipo de mensagem */}
                                                    <label className="robo-context-label" htmlFor="msg-type-select" style={{ marginBottom: 6 }}>
                                                        {t('messages.step1')}
                                                    </label>
                                                    <select
                                                        id="msg-type-select"
                                                        value={selectedMessageType}
                                                        onChange={(e) => setSelectedMessageType(e.target.value)}
                                                        style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: '2px solid #e2e8f0', fontSize: 14, fontWeight: 600, color: '#1e293b', background: '#fff', marginBottom: 18, cursor: 'pointer' }}
                                                    >
                                                        {messageFields.map((f) => (
                                                            <option key={f.key} value={f.key}>{f.emoji}  {f.label}</option>
                                                        ))}
                                                    </select>

                                                    {/* Editor da mensagem selecionada */}
                                                    {(() => {
                                                        const field = messageFields.find((f) => f.key === selectedMessageType);
                                                        if (!field) return null;
                                                        const isDefault = (customMessages[field.key] || '').trim() === (field.defaultTemplate || '').trim();
                                                        const lojaName = empresaParse?.name;
                                                        const previewText = fillPreviewVars(customMessages[field.key], lojaName, previewExample);
                                                        return (
                                                            <div>
                                                                {/* Quando esta mensagem é enviada */}
                                                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 12px', marginBottom: 16, fontSize: 13, color: '#92400e' }}>
                                                                    <span style={{ fontSize: 16, lineHeight: 1.4 }}>{field.emoji}</span>
                                                                    <span><strong>{t('messages.whenLabel')}</strong> {field.when}</span>
                                                                </div>

                                                                {/* 2. Variáveis */}
                                                                <label className="robo-context-label" style={{ marginBottom: 6 }}>
                                                                    {t('messages.step2')}
                                                                </label>
                                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                                                                    {messageVars.map((v) => (
                                                                        <button
                                                                            type="button"
                                                                            key={v.tag}
                                                                            onClick={() => insertVariable(field.key, v.tag)}
                                                                            title={t('messages.insertVarTitle', { label: v.label, example: v.example })}
                                                                            style={{ cursor: 'pointer', border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4338ca', borderRadius: 999, padding: '5px 12px', fontSize: 12, fontWeight: 700 }}
                                                                        >
                                                                            + {v.tag}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', marginBottom: 16 }}>
                                                                    {messageVars.map((v) => (
                                                                        <div key={v.tag} style={{ fontSize: 12.5, color: '#475569', lineHeight: 1.9 }}>
                                                                            <code style={{ background: '#eef2ff', color: '#4338ca', padding: '1px 6px', borderRadius: 6, fontWeight: 700 }}>{v.tag}</code> = {v.label} <span style={{ color: '#94a3b8' }}>→ {v.example}</span>
                                                                        </div>
                                                                    ))}
                                                                </div>

                                                                {/* 3. Texto da mensagem */}
                                                                <label className="robo-context-label" style={{ marginBottom: 6 }}>
                                                                    {t('messages.step3')}
                                                                </label>
                                                                <textarea
                                                                    ref={(el) => { messageRefs.current[field.key] = el; }}
                                                                    className="robo-context-textarea"
                                                                    value={customMessages[field.key]}
                                                                    onChange={(e) => setCustomMessages((prev) => ({ ...prev, [field.key]: e.target.value.slice(0, CUSTOM_MSG_MAX) }))}
                                                                    placeholder={field.defaultTemplate}
                                                                    rows={4}
                                                                    maxLength={CUSTOM_MSG_MAX}
                                                                    disabled={customMessagesSaving}
                                                                />

                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, flexWrap: 'wrap', gap: 8 }}>
                                                                    <span style={{ fontSize: 11, color: '#94a3b8' }}>
                                                                        {t('messages.charCount', { count: customMessages[field.key]?.length || 0, max: CUSTOM_MSG_MAX })}
                                                                    </span>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleRestoreDefault(field.key)}
                                                                        disabled={isDefault || customMessagesSaving}
                                                                        title={t('messages.restoreTitle')}
                                                                        style={{ cursor: isDefault ? 'default' : 'pointer', border: '1px solid #e2e8f0', background: isDefault ? '#f8fafc' : '#fff', color: isDefault ? '#cbd5e1' : '#475569', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                                                                    >
                                                                        <FaUndo /> {t('messages.restore')}
                                                                    </button>
                                                                </div>

                                                                {/* AVISO CHAMATIVO: não esquecer de salvar */}
                                                                <div style={{
                                                                    marginTop: 16,
                                                                    background: isCustomMessagesDirty ? '#fef3c7' : '#fffbeb',
                                                                    border: `2px solid ${isCustomMessagesDirty ? '#f59e0b' : '#fde68a'}`,
                                                                    borderRadius: 10,
                                                                    padding: '14px 16px',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'space-between',
                                                                    gap: 12,
                                                                    flexWrap: 'wrap',
                                                                    boxShadow: isCustomMessagesDirty ? '0 0 0 4px rgba(245, 158, 11, 0.18)' : 'none'
                                                                }}>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#92400e', fontSize: 13.5, fontWeight: 700, flex: 1, minWidth: 220 }}>
                                                                        <span style={{ fontSize: 24, lineHeight: 1 }}>⚠️</span>
                                                                        <span>
                                                                            {isCustomMessagesDirty ? t('messages.warningDirty') : t('messages.warningClean')}
                                                                        </span>
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        onClick={handleSaveCustomMessages}
                                                                        disabled={customMessagesSaving || !isCustomMessagesDirty}
                                                                        title={isCustomMessagesDirty ? t('messages.saveTitleDirty') : t('messages.saveTitleClean')}
                                                                        style={{
                                                                            cursor: (customMessagesSaving || !isCustomMessagesDirty) ? 'default' : 'pointer',
                                                                            border: 'none',
                                                                            background: (customMessagesSaving || !isCustomMessagesDirty) ? '#fcd34d' : '#f59e0b',
                                                                            color: '#fff',
                                                                            borderRadius: 8,
                                                                            padding: '10px 18px',
                                                                            fontSize: 13,
                                                                            fontWeight: 700,
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            gap: 8,
                                                                            whiteSpace: 'nowrap'
                                                                        }}
                                                                    >
                                                                        <FaSave /> {customMessagesSaving ? t('messages.saving') : t('messages.save')}
                                                                    </button>
                                                                </div>

                                                                {/* Prévia estilo WhatsApp */}
                                                                <label className="robo-context-label" style={{ marginTop: 16, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                    <FaWhatsapp style={{ color: '#22c55e' }} /> {t('messages.previewLabel')}
                                                                </label>
                                                                <div style={{ background: '#e5ddd5', borderRadius: 12, padding: 16 }}>
                                                                    <div style={{ background: '#dcf8c6', borderRadius: 10, padding: '10px 12px', maxWidth: 440, marginLeft: 'auto', boxShadow: '0 1px 1px rgba(0,0,0,0.12)', fontSize: 14, color: '#111b21', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                                                                        {previewText.trim()
                                                                            ? previewText.split('\n').map((line, i) => (<div key={i}>{renderWhatsappBold(line, `pv-${i}`)}</div>))
                                                                            : <span style={{ color: '#667781' }}>{t('messages.previewEmpty')}</span>}
                                                                        <div style={{ textAlign: 'right', fontSize: 10, color: '#667781', marginTop: 4 }}>{t('messages.previewNow')} ✓✓</div>
                                                                    </div>
                                                                </div>
                                                                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                                                                    {t('messages.previewExampleNote', { numero: previewExample.numeroPedido, cliente: previewExample.cliente, total: previewExample.total })}
                                                                </div>
                                                            </div>
                                                        );
                                                    })()}

                                                    <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '12px 14px', marginTop: 18, fontSize: 13, color: '#1e40af' }}>
                                                        <div style={{ fontWeight: 700, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                                                            <FaInfoCircle /> {t('messages.tips.title')}
                                                        </div>
                                                        <div style={{ lineHeight: 1.9 }}>
                                                            <div>• {t('messages.tips.item1')}</div>
                                                            <div>• {t('messages.tips.item2')}</div>
                                                            <div>• {t('messages.tips.item3')}</div>
                                                            <div>• {t('messages.tips.item4')}</div>
                                                        </div>
                                                    </div>

                                                    <div className="robo-context-actions">
                                                        <button
                                                            className="robo-context-save-btn"
                                                            onClick={handleSaveCustomMessages}
                                                            disabled={customMessagesSaving || !isCustomMessagesDirty}
                                                        >
                                                            {customMessagesSaving ? (
                                                                <span className="robo-toggle-loading">
                                                                    <div className="robo-mini-spinner"></div>
                                                                    {t('messages.saving')}
                                                                </span>
                                                            ) : (
                                                                <>
                                                                    <FaSave /> {t('messages.save')}
                                                                </>
                                                            )}
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ============ ABA RESPOSTAS PERSONALIZADAS ============ */}
                            {activeTab === 'responses' && (
                                <div className="robo-respostas-container-inner">
                                    <div className="robo-respostas-header">
                                        <div className="robo-header-content">
                                            <h4>{t('responses.title')}</h4>
                                            <p>{t('responses.subtitle')}</p>
                                        </div>
                                        <button
                                            className="robo-btn-nova-resposta"
                                            onClick={() => setIsAddModalOpen(true)}
                                        >
                                            <FaPlus /> {t('responses.newResponse')}
                                        </button>
                                    </div>

                                    <div className="robo-respostas-search">
                                        <div className="robo-search-input-container">
                                            <FaSearch className="robo-search-icon" />
                                            <input
                                                type="text"
                                                placeholder={t('responses.searchPlaceholder')}
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                className="robo-search-input"
                                            />
                                        </div>
                                    </div>

                                    <div className="robo-respostas-content">
                                        {loading ? (
                                            <div className="robo-loading-container">
                                                <div className="robo-loading-spinner"></div>
                                                <p>{t('responses.loading')}</p>
                                            </div>
                                        ) : filteredResponses.length === 0 ? (
                                            <div className="robo-empty-state">
                                                <div className="robo-empty-icon">
                                                    <FaComments />
                                                </div>
                                                <h3>{t('responses.empty.title')}</h3>
                                                <p>
                                                    {searchTerm
                                                        ? t('responses.empty.searchMessage')
                                                        : t('responses.empty.defaultMessage')
                                                    }
                                                </p>
                                                {!searchTerm && (
                                                    <button
                                                        className="robo-btn-add-first"
                                                        onClick={() => setIsAddModalOpen(true)}
                                                    >
                                                        <FaPlus /> {t('responses.empty.addFirst')}
                                                    </button>
                                                )}
                                            </div>
                                        ) : (
                                            <>
                                                <div className="robo-respostas-list">
                                                    {currentItems.map((responseObj, index) => {
                                                        const parsed = parseCustomResponse(responseObj);
                                                        return (
                                                            <div key={responseObj.id} className={`robo-resposta-card ${!responseObj.active ? 'inactive' : ''}`}>
                                                                <div className="robo-card-header">
                                                                    <div className="robo-card-status">
                                                                        <div className={`robo-status-indicator ${responseObj.active ? 'active' : 'inactive'}`}>
                                                                            {responseObj.active ? <FaRobot /> : <FaRobot />}
                                                                        </div>
                                                                        <span className="robo-status-text">
                                                                            {responseObj.active ? t('responses.card.active') : t('responses.card.inactive')}
                                                                        </span>
                                                                    </div>
                                                                    <div className="robo-card-number">
                                                                        #{(currentPage - 1) * itemsPerPage + index + 1}
                                                                    </div>
                                                                </div>

                                                                <div className="robo-resposta-content">
                                                                    <div className="robo-resposta-question">
                                                                        <div className="robo-section-header">
                                                                            <MdQuestionAnswer className="robo-section-icon" />
                                                                            <span className="robo-question-label">{t('responses.card.whenToUse')}</span>
                                                                        </div>
                                                                        <p className="robo-question-text">{parsed.question}</p>
                                                                    </div>

                                                                    <div className="robo-resposta-response">
                                                                        <div className="robo-section-header">
                                                                            <BiMessageDetail className="robo-section-icon" />
                                                                            <span className="robo-response-label">{t('responses.card.instructions')}</span>
                                                                        </div>
                                                                        <p className="robo-response-text">{parsed.response}</p>
                                                                    </div>
                                                                </div>

                                                                <div className="robo-resposta-actions">
                                                                    <button
                                                                        className={`robo-btn-toggle ${responseObj.active ? 'active' : 'inactive'}`}
                                                                        onClick={() => handleToggleActive(responseObj)}
                                                                        title={responseObj.active ? t('responses.tooltips.deactivate') : t('responses.tooltips.activate')}
                                                                    >
                                                                        {responseObj.active ? <FaToggleOn /> : <FaToggleOff />}
                                                                    </button>
                                                                    <button
                                                                        className="robo-btn-edit"
                                                                        onClick={() => openEditModal(responseObj)}
                                                                        title={t('responses.tooltips.edit')}
                                                                    >
                                                                        <FaEdit />
                                                                    </button>
                                                                    <button
                                                                        className="robo-btn-delete"
                                                                        onClick={() => confirmDelete(responseObj)}
                                                                        title={t('responses.tooltips.delete')}
                                                                    >
                                                                        <FaTrash />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>

                                                {/* Paginacao */}
                                                {totalPages > 1 && (
                                                    <div className="robo-pagination">
                                                        <button
                                                            className="robo-pagination-btn"
                                                            onClick={() => paginate(currentPage - 1)}
                                                            disabled={currentPage === 1}
                                                        >
                                                            {t('responses.pagination.previous')}
                                                        </button>

                                                        <div className="robo-pagination-numbers">
                                                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                                                                <button
                                                                    key={number}
                                                                    className={`robo-pagination-number ${currentPage === number ? 'active' : ''}`}
                                                                    onClick={() => paginate(number)}
                                                                >
                                                                    {number}
                                                                </button>
                                                            ))}
                                                        </div>

                                                        <button
                                                            className="robo-pagination-btn"
                                                            onClick={() => paginate(currentPage + 1)}
                                                            disabled={currentPage === totalPages}
                                                        >
                                                            {t('responses.pagination.next')}
                                                        </button>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </ContentWrapper>
                </PageContainer>

                {/* Modais */}
                <ModalAddCustomResponse
                    isOpen={isAddModalOpen}
                    onClose={() => setIsAddModalOpen(false)}
                    onSubmit={handleAddResponse}
                />

                <ModalEditCustomResponse
                    isOpen={isEditModalOpen}
                    onClose={() => {
                        setIsEditModalOpen(false);
                        setEditingData(null);
                    }}
                    onSubmit={handleEditResponse}
                    initialData={editingData}
                />

                {/* Modal de confirmacao de exclusao */}
                <Modal isOpen={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)}>
                    <div className="robo-delete-confirm-modal">
                        <div className="robo-delete-confirm-header">
                            <div className="robo-delete-icon">
                                <FaTrash />
                            </div>
                            <h3>{t('deleteModal.title')}</h3>
                        </div>
                        <p>{t('deleteModal.message')}</p>
                        <div className="robo-delete-confirm-actions">
                            <button
                                className="robo-btn-cancel-delete"
                                onClick={() => {
                                    setShowDeleteConfirm(false);
                                    setDeleteIndex(null);
                                }}
                            >
                                {t('deleteModal.cancel')}
                            </button>
                            <button
                                className="robo-btn-confirm-delete"
                                onClick={handleDeleteResponse}
                            >
                                {t('deleteModal.confirm')}
                            </button>
                        </div>
                    </div>
                </Modal>
            </PermissionGate>
        </>
    )
}

export default RoboCfg;
